import { FC, Dispatch, SetStateAction, MutableRefObject } from "react";
import {
  Box,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import DropDown from "@/components/forms/inputs/dropDown";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { handleAnchorDataTemplateChange } from "@/components/anchorDataOverview/anchorDataOverviewUtils";
import { useTranslation } from "react-i18next";
import styles from "@/components/anchorDataOverview/anchorDataOverview.module.css";

type AnchorDataOverviewAccordionProps = {
  selectedAnchorDataTemplates: (any | null)[];
  lastSelectedTemplates: (any | null)[];
  templateSlotIds: string[];
  anchorDataTemplateOptions: any[];
  setSelectedAnchorDataTemplates: Dispatch<SetStateAction<(any | null)[]>>;
  setLastSelectedTemplates: Dispatch<SetStateAction<(any | null)[]>>;
  setTemplateSlotIds: Dispatch<SetStateAction<string[]>>;
  templateSlotIdCounter: MutableRefObject<number>;
  setReorderCounter: Dispatch<SetStateAction<number>>;
  maxColumns: number;
  columnKeysWithPendingEdits: Set<string>;
  onRemoveLastColumn: () => void;
  disableRemoveLastColumn?: boolean;
};

const AnchorDataOverviewAccordion: FC<AnchorDataOverviewAccordionProps> = ({
  selectedAnchorDataTemplates,
  lastSelectedTemplates,
  templateSlotIds,
  anchorDataTemplateOptions,
  setSelectedAnchorDataTemplates,
  setLastSelectedTemplates,
  setTemplateSlotIds,
  templateSlotIdCounter,
  setReorderCounter,
  maxColumns,
  columnKeysWithPendingEdits,
  onRemoveLastColumn,
  disableRemoveLastColumn = false,
}) => {
  const { t } = useTranslation();

  const handleAddColumn = () => {
    if (selectedAnchorDataTemplates.length < maxColumns) {
      setSelectedAnchorDataTemplates((prev) => [...prev, null]);
      setLastSelectedTemplates((prev) => [...prev, null]);
      setTemplateSlotIds((prev) => [
        ...prev,
        `slot-${templateSlotIdCounter.current++}`,
      ]);
    }
  };

  const handleRemoveColumn = (idx: number) => {
    const def = selectedAnchorDataTemplates[idx] ?? lastSelectedTemplates[idx];
    const columnKey = def ? `value_${def.value}` : null;
    const hasPendingEditsInColumn =
      !!columnKey && columnKeysWithPendingEdits.has(columnKey);
    const isLastColumnDeleteDisabled =
      disableRemoveLastColumn && selectedAnchorDataTemplates.length <= 1;
    if (hasPendingEditsInColumn || isLastColumnDeleteDisabled) return;
    if (selectedAnchorDataTemplates.length <= 1) {
      onRemoveLastColumn();
      return;
    }
    setSelectedAnchorDataTemplates((prev) => prev.filter((_, i) => i !== idx));
    setLastSelectedTemplates((prev) => prev.filter((_, i) => i !== idx));
    setTemplateSlotIds((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(selectedAnchorDataTemplates);
    const lastItems = Array.from(lastSelectedTemplates);
    const slotItems = Array.from(templateSlotIds);

    const [reorderedItem] = items.splice(result.source.index, 1);
    const [reorderedLastItem] = lastItems.splice(result.source.index, 1);
    const [reorderedSlot] = slotItems.splice(result.source.index, 1);

    items.splice(result.destination.index, 0, reorderedItem);
    lastItems.splice(result.destination.index, 0, reorderedLastItem);
    slotItems.splice(result.destination.index, 0, reorderedSlot);

    setSelectedAnchorDataTemplates(items);
    setLastSelectedTemplates(lastItems);
    setTemplateSlotIds(slotItems);
    setReorderCounter((prev) => prev + 1);
  };

  const getFilteredOptions = () => {
    const selectedValues = Array.isArray(selectedAnchorDataTemplates)
      ? selectedAnchorDataTemplates.map((selected: any) => selected?.value)
      : [];
    return anchorDataTemplateOptions.map((option: any) => ({
      ...option,
      isDisabled: selectedValues.includes(option.value),
    }));
  };

  return (
    <Stack direction="column" spacing={1} className={styles.dropdownArea}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{t("anchor_data_templates")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="anchorDataTemplates">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {templateSlotIds.map((slotId, idx) => {
                      const template = selectedAnchorDataTemplates[idx];
                      const def =
                        selectedAnchorDataTemplates[idx] ??
                        lastSelectedTemplates[idx];
                      const columnKey = def ? `value_${def.value}` : null;
                      const hasPendingEditsInColumn =
                        !!columnKey &&
                        columnKeysWithPendingEdits.has(columnKey);
                      const isLastColumnDeleteDisabled =
                        disableRemoveLastColumn &&
                        selectedAnchorDataTemplates.length <= 1;
                      const isDeleteDisabled =
                        hasPendingEditsInColumn || isLastColumnDeleteDisabled;
                      return (
                        <Draggable
                          key={slotId}
                          draggableId={slotId}
                          index={idx}
                          isDragDisabled={selectedAnchorDataTemplates.length <= 1}
                        >
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                            >
                              <Box
                                className={styles.anchorDataTemplateDropDownRow}
                              >
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  className={styles.dragHandle}
                                >
                                  <DragIndicatorIcon />
                                </div>

                                <DropDown
                                  options={getFilteredOptions()}
                                  onChange={(opt) =>
                                    handleAnchorDataTemplateChange(
                                      opt,
                                      idx,
                                      setSelectedAnchorDataTemplates,
                                      setLastSelectedTemplates,
                                    )
                                  }
                                  placeholder={t("search_placeholder")}
                                  isSearchable={true}
                                  defaultValue={template?.value}
                                  isClearable={true}
                                />
                                <div
                                  className={
                                    isDeleteDisabled
                                      ? styles.cursorNotAllowed
                                      : styles.cursorPointer
                                  }
                                >
                                  <Tooltip
                                    title={
                                      hasPendingEditsInColumn
                                        ? t("anchordata_overview_column_has_pending_inline_edits")
                                        : isLastColumnDeleteDisabled
                                          ? undefined
                                          : t("remove_anchor_data_template_column")
                                    }
                                  >
                                    <span>
                                      <GenericButton
                                        variant="icon"
                                        className={styles.removeColumnButton}
                                        onClick={() => handleRemoveColumn(idx)}
                                        disabled={isDeleteDisabled}
                                      >
                                        <DeleteIcon />
                                      </GenericButton>
                                    </span>
                                  </Tooltip>
                                </div>
                              </Box>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <Box className={styles.addColumnButtonContainer}>
              <GenericButton
                color="primary"
                onClick={handleAddColumn}
                disabled={selectedAnchorDataTemplates.length === maxColumns}
              >
                <span className={styles.addColumnButtonContent}>
                  <AddIcon />
                  {t("add_anchor_data_template_column")}
                </span>
              </GenericButton>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default AnchorDataOverviewAccordion;
